import React, { useEffect, useState } from "react";
import { Instagram, MapPin } from "lucide-react";
import AuroraMark from "@/components/AuroraMark";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

function WhatsAppIcon({ size = 18, className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01zM12.05 20.15h-.01a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.24 8.24 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.42 5.83c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42-.14-.01-.31-.01-.48-.01s-.43.06-.66.31c-.23.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.17 1.74 2.65 4.22 3.72.59.25 1.05.4 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29z"/>
    </svg>
  );
}

export default function Footer() {
  const { t } = useI18n();
  const [contact, setContact] = useState({
    whatsapp: "087792682795",
    whatsapp_display: "0877-9268-2795",
    instagram: "aurorahijab.co",
    tiktok: "aurorahijab_official",
    address:
      "Perumahan Greenwood, Jl. Seruni II No.4, Sadeng, Kec. Gn. Pati, Kota Semarang, Jawa Tengah 50222",
  });

  useEffect(() => {
    api
      .get("/content/contact")
      .then((r) => {
        if (r.data && Object.keys(r.data).length) {
          setContact((prev) => ({ ...prev, ...r.data }));
        }
      })
      .catch(() => {});
  }, []);

  const waIntl = "62" + (contact.whatsapp || "").replace(/^0/, "");
  return (
    <footer
      data-testid="site-footer"
      id="kontak"
      className="bg-[#0A0A0A] text-stone-100 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10 md:pb-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-12 md:mb-20">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <AuroraMark size="h-20 w-20" />
              <div>
                <div className="font-display text-2xl text-white">
                  Aurora <span className="text-[#F39237]">Hijab</span>
                </div>
                <div className="overline text-[#F39237]">
                  Simple &amp; Adorable
                </div>
              </div>
            </div>
            <p className="text-stone-400 text-sm leading-relaxed max-w-md">
              {t(
                "footer.desc",
                "Aurora Hijab adalah produsen home dress & gamis muslimah yang berbasis di Semarang. Kami melayani reseller, agen, dan distributor di seluruh Indonesia dengan kualitas jahit yang terjaga."
              )}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="overline text-[#F39237] mb-5">{t("footer.nav_title", "Navigasi")}</div>
            <ul className="space-y-3 text-sm text-stone-300">
              <li>
                <a href="/#katalog" data-testid="footer-link-catalog" className="hover:text-[#F39237]">
                  {t("footer.nav_catalog", "Katalog")}
                </a>
              </li>
              <li>
                <a href="/#tier" data-testid="footer-link-program" className="hover:text-[#F39237]">
                  {t("footer.nav_program", "Program Reseller")}
                </a>
              </li>
              <li>
                <a href="/#workshop" data-testid="footer-link-workshop" className="hover:text-[#F39237]">
                  {t("footer.nav_workshop", "Workshop")}
                </a>
              </li>
              <li>
                <a href="/e-learning" data-testid="footer-link-elearning" className="hover:text-[#F39237]">
                  {t("footer.nav_elearning", "E-Learning")}
                </a>
              </li>
              <li>
                <a href="/kritik-saran" data-testid="footer-link-feedback" className="hover:text-[#F39237]">
                  {t("footer.nav_feedback", "Kritik & Saran")}
                </a>
              </li>
              <li>
                <a href="/#daftar" data-testid="footer-link-register" className="hover:text-[#F39237]">
                  {t("footer.nav_register", "Daftar Mitra")}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline text-[#F39237] mb-5">{t("footer.contact_title", "Hubungi Kami")}</div>
            <ul className="space-y-4 text-sm text-stone-200">
              <li className="flex items-start gap-3">
                <WhatsAppIcon
                  size={18}
                  className="text-[#F39237] mt-0.5 flex-shrink-0"
                />
                <a
                  href={`https://wa.me/${waIntl}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp"
                  className="hover:text-[#F39237]"
                >
                  {contact.whatsapp_display || contact.whatsapp}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Instagram
                  size={18}
                  className="text-[#F39237] mt-0.5 flex-shrink-0"
                />
                <a
                  href={`https://instagram.com/${contact.instagram}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-instagram"
                  className="hover:text-[#F39237]"
                >
                  @{contact.instagram}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="currentColor"
                  className="text-[#F39237] mt-0.5 flex-shrink-0"
                >
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
                </svg>
                <a
                  href={`https://www.tiktok.com/@${contact.tiktok}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-tiktok"
                  className="hover:text-[#F39237]"
                >
                  @{contact.tiktok}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin
                  size={18}
                  className="text-[#F39237] mt-0.5 flex-shrink-0"
                />
                <span data-testid="footer-address">{contact.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-800 pt-8">
          <div className="select-none">
            <div className="font-display italic text-[20vw] md:text-[14vw] leading-none text-[#F39237]/15 tracking-tighter">
              Aurora
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mt-8 gap-4 text-xs text-stone-500">
            <div>
              © {new Date().getFullYear()} Aurora Hijab. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              <span>{t("footer.made_in", "Made in Semarang, Indonesia")}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
